import { useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';

/**
 * WhatsApp's own text formatting, both ways.
 *
 * WhatsApp has no rich text — it has four markers a person types around a word:
 * *bold*, _italic_, ~strikethrough~ and ```monospace```. A template body, a
 * quick reply or a chat message is stored exactly as typed, markers and all,
 * and the phone draws them. So "make text bold" here means: put the markers
 * in; and the previews, which promised to draw the message the way the phone
 * does, have to honour them. Two exports, one per direction.
 */

// ------------------------------------------------------------- rendering --

const MARKERS: { open: string; close: string; wrap: (inner: ReactNode, key: number) => ReactNode }[] = [
  { open: '```', close: '```', wrap: (inner, key) => <code key={key} className="font-mono text-[0.95em]">{inner}</code> },
  { open: '*', close: '*', wrap: (inner, key) => <strong key={key}>{inner}</strong> },
  { open: '_', close: '_', wrap: (inner, key) => <em key={key}>{inner}</em> },
  { open: '~', close: '~', wrap: (inner, key) => <s key={key}>{inner}</s> },
];

/**
 * Text with WhatsApp markers → React nodes, the way the phone shows it.
 *
 * The rule WhatsApp itself applies: a marker opens only when the character
 * before it is not a letter or digit, and closes only when the one after it
 * is not — so an asterisk in "2*3*4" or an underscore in "first_name" stays as
 * typed. Unmatched markers are left as they are. Placeholders like {{1}} pass
 * through untouched.
 */
export function renderWhatsAppText(text: string): ReactNode {
  return render(text, 0).nodes;
}

function render(text: string, depth: number): { nodes: ReactNode[] } {
  const nodes: ReactNode[] = [];
  let plain = '';
  let i = 0;
  let key = 0;

  while (i < text.length) {
    let matched = false;

    if (depth < 3) {
      for (const marker of MARKERS) {
        if (!text.startsWith(marker.open, i)) continue;
        if (!opensHere(text, i)) continue;

        const close = findClose(text, i + marker.open.length, marker.close);
        if (close < 0) continue;

        const inner = text.slice(i + marker.open.length, close);
        if (inner.length === 0) continue;

        if (plain) { nodes.push(plain); plain = ''; }
        nodes.push(marker.wrap(render(inner, depth + 1).nodes, key++));
        i = close + marker.close.length;
        matched = true;
        break;
      }
    }

    if (!matched) { plain += text[i]; i += 1; }
  }

  if (plain) nodes.push(plain);
  return { nodes };
}

function isWordChar(c: string | undefined): boolean {
  return c !== undefined && /[\p{L}\p{N}]/u.test(c);
}

function opensHere(text: string, at: number): boolean {
  return !isWordChar(text[at - 1]);
}

/** The closing marker: not followed by a word character, not preceded by a space, and not the first char after the opener. */
function findClose(text: string, from: number, close: string): number {
  let at = text.indexOf(close, from);
  while (at >= 0) {
    const after = text[at + close.length];
    const before = text[at - 1];
    // "* text*" is not bold on the phone either: no space just inside a marker.
    if (!isWordChar(after) && before !== ' ' && at > from) return at;
    at = text.indexOf(close, at + 1);
  }
  return -1;
}

// ------------------------------------------------------------- toolbar ----

/** Wraps the selection in a textarea with a marker (or unwraps it when already wrapped). Returns the new value and where the caret should go. */
export function toggleMarker(value: string, start: number, end: number, marker: string): { value: string; start: number; end: number } {
  const before = value.slice(0, start);
  const selected = value.slice(start, end);
  const after = value.slice(end);

  // Already wrapped: take the markers off.
  if (before.endsWith(marker) && after.startsWith(marker)) {
    return {
      value: before.slice(0, -marker.length) + selected + after.slice(marker.length),
      start: start - marker.length,
      end: end - marker.length,
    };
  }
  if (selected.startsWith(marker) && selected.endsWith(marker) && selected.length >= marker.length * 2) {
    const inner = selected.slice(marker.length, -marker.length);
    return { value: before + inner + after, start, end: start + inner.length };
  }

  // Nothing selected: wrap the word under the caret, or insert a pair to type into.
  if (selected.length === 0) {
    const wordStart = before.search(/[\p{L}\p{N}]+$/u);
    const wordEndMatch = /^[\p{L}\p{N}]+/u.exec(after);
    if (wordStart >= 0 || wordEndMatch) {
      const s = wordStart >= 0 ? wordStart : start;
      const e = end + (wordEndMatch?.[0].length ?? 0);
      return toggleMarker(value, s, e, marker);
    }
    return { value: before + marker + marker + after, start: start + marker.length, end: start + marker.length };
  }

  // Keep surrounding spaces outside the markers — "*word *" does not render.
  const lead = selected.length - selected.trimStart().length;
  const trail = selected.length - selected.trimEnd().length;
  const core = selected.trim();
  const wrapped = selected.slice(0, lead) + marker + core + marker + selected.slice(selected.length - trail);
  return { value: before + wrapped + after, start: start + lead, end: start + lead + marker.length * 2 + core.length };
}

/** Puts text at the caret (replacing any selection). */
export function insertAtCaret(value: string, start: number, end: number, text: string): { value: string; caret: number } {
  return { value: value.slice(0, start) + text + value.slice(end), caret: start + text.length };
}

interface ToolbarProps {
  /** The textarea the buttons act on. */
  box: RefObject<HTMLTextAreaElement | null>;
  value: string;
  onChange: (next: string) => void;
  /** Leave out to hide the emoji button. */
  emoji?: boolean;
  className?: string;
}

/**
 * Bold, italic, strikethrough, monospace and an emoji picker for a textarea.
 *
 * Buttons, not a contentEditable: the value stays a plain string with
 * WhatsApp's markers in it, which is what is sent, stored and reviewed. The
 * toolbar edits the string around the selection and puts the caret back.
 */
export function FormatToolbar({ box, value, onChange, emoji = true, className = '' }: ToolbarProps) {
  const [pickerOpen, setPickerOpen] = useState(false);

  function apply(marker: string) {
    const el = box.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? start;
    const next = toggleMarker(value, start, end, marker);
    onChange(next.value);
    queueMicrotask(() => { el?.focus(); el?.setSelectionRange(next.start, next.end); });
  }

  function insert(text: string) {
    const el = box.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? start;
    const next = insertAtCaret(value, start, end, text);
    onChange(next.value);
    queueMicrotask(() => { el?.focus(); el?.setSelectionRange(next.caret, next.caret); });
  }

  const button = 'tap grid h-7 min-w-7 place-items-center rounded px-1.5 text-small text-(--color-ink-soft) hover:bg-(--color-sunken) hover:text-(--color-ink)';

  return (
    <div className={`relative flex flex-wrap items-center gap-0.5 ${className}`} role="toolbar" aria-label="Formatting">
      <button type="button" onClick={() => apply('*')} className={`${button} font-bold`} title="Bold — *text*" aria-label="Bold">B</button>
      <button type="button" onClick={() => apply('_')} className={`${button} italic`} title="Italic — _text_" aria-label="Italic">I</button>
      <button type="button" onClick={() => apply('~')} className={`${button} line-through`} title="Strikethrough — ~text~" aria-label="Strikethrough">S</button>
      <button type="button" onClick={() => apply('```')} className={`${button} font-mono`} title="Monospace — ```text```" aria-label="Monospace">{'<>'}</button>
      {emoji && (
        <>
          <button type="button" onClick={() => setPickerOpen((v) => !v)} className={button} title="Emoji" aria-label="Emoji" aria-expanded={pickerOpen}>
            <span aria-hidden="true">😊</span>
          </button>
          {pickerOpen && <EmojiPicker onPick={(e) => { insert(e); setPickerOpen(false); }} onClose={() => setPickerOpen(false)} />}
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------- emoji ------

/**
 * A small picker rather than a library: the full Unicode set is thousands of
 * glyphs and a megabyte of names, and what a business types into a template is
 * a greeting, a tick, a pointer, a heart. Grouped the way phones group them,
 * searchable by the short names below.
 */
const EMOJI_GROUPS: { label: string; items: [string, string][] }[] = [
  { label: 'Smileys', items: [
    ['😀', 'grin'], ['😃', 'smile'], ['😄', 'happy'], ['😁', 'beam'], ['😆', 'laugh'], ['😅', 'sweat smile'], ['🤣', 'rofl'], ['😂', 'joy'],
    ['🙂', 'slight smile'], ['😉', 'wink'], ['😊', 'blush'], ['😇', 'halo'], ['🥰', 'love'], ['😍', 'heart eyes'], ['🤩', 'star struck'], ['😘', 'kiss'],
    ['😋', 'yum'], ['😎', 'cool'], ['🤗', 'hug'], ['🤔', 'thinking'], ['🤫', 'shush'], ['😐', 'neutral'], ['😴', 'sleep'], ['🥳', 'party'],
    ['😢', 'cry'], ['😭', 'sob'], ['😡', 'angry'], ['🙏', 'pray thanks namaste'], ['🤝', 'handshake'], ['💪', 'strong'], ['🎉', 'tada celebrate'], ['🔥', 'fire'],
  ] },
  { label: 'Gestures', items: [
    ['👍', 'thumbs up'], ['👎', 'thumbs down'], ['👏', 'clap'], ['🙌', 'raised hands'], ['👋', 'wave hello'], ['✌️', 'victory'], ['🤞', 'fingers crossed'], ['👌', 'ok'],
    ['👉', 'point right'], ['👈', 'point left'], ['👇', 'point down'], ['👆', 'point up'], ['☝️', 'index up'], ['✋', 'stop hand'], ['🤙', 'call me'], ['💯', 'hundred'],
  ] },
  { label: 'Hearts & marks', items: [
    ['❤️', 'red heart'], ['🧡', 'orange heart'], ['💛', 'yellow heart'], ['💚', 'green heart'], ['💙', 'blue heart'], ['💜', 'purple heart'], ['🤍', 'white heart'], ['💖', 'sparkling heart'],
    ['✅', 'check tick'], ['☑️', 'ballot check'], ['✔️', 'check mark'], ['❌', 'cross'], ['⭐', 'star'], ['🌟', 'glowing star'], ['✨', 'sparkles'], ['⚡', 'lightning'],
    ['❗', 'exclamation'], ['❓', 'question'], ['⚠️', 'warning'], ['🚫', 'no'], ['🔴', 'red circle'], ['🟢', 'green circle'], ['🔵', 'blue circle'], ['▶️', 'play'],
  ] },
  { label: 'Business', items: [
    ['📞', 'phone call'], ['📱', 'mobile'], ['💬', 'chat'], ['📧', 'email'], ['📩', 'envelope'], ['📅', 'calendar'], ['⏰', 'alarm clock'], ['⏳', 'hourglass'],
    ['📍', 'location pin'], ['🏠', 'home'], ['🏢', 'office'], ['🚚', 'delivery truck'], ['📦', 'package parcel'], ['🛒', 'cart'], ['🛍️', 'shopping bags'], ['🎁', 'gift'],
    ['💰', 'money bag'], ['💳', 'card'], ['🧾', 'receipt invoice'], ['💵', 'cash'], ['📈', 'chart up'], ['📉', 'chart down'], ['📊', 'bar chart'], ['📋', 'clipboard'],
    ['📝', 'memo note'], ['📄', 'document'], ['🔗', 'link'], ['🔒', 'lock'], ['🔑', 'key'], ['⚙️', 'gear settings'], ['🛠️', 'tools'], ['💡', 'idea bulb'],
    ['🎯', 'target'], ['🏆', 'trophy'], ['🥇', 'gold medal'], ['🎊', 'confetti'], ['🎂', 'cake birthday'], ['🎈', 'balloon'], ['🪔', 'diya diwali'], ['🙏🏽', 'namaste'],
  ] },
  { label: 'Travel & food', items: [
    ['✈️', 'plane'], ['🚗', 'car'], ['🚕', 'taxi'], ['🚌', 'bus'], ['🚆', 'train'], ['🏨', 'hotel'], ['🌍', 'globe'], ['☀️', 'sun'],
    ['🌧️', 'rain'], ['🌙', 'moon'], ['☕', 'coffee'], ['🍕', 'pizza'], ['🍔', 'burger'], ['🍰', 'cake slice'], ['🍽️', 'plate'], ['🥗', 'salad'],
  ] },
];

export function EmojiPicker({ onPick, onClose }: { onPick: (emoji: string) => void; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);

  useEffect(() => {
    search.current?.focus();
    function away(e: MouseEvent) { if (root.current && !root.current.contains(e.target as Node)) onClose(); }
    function key(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', key); };
  }, [onClose]);

  const term = query.trim().toLowerCase();
  const groups = useMemo(
    () => EMOJI_GROUPS
      .map((g) => ({ label: g.label, items: term ? g.items.filter(([, name]) => name.includes(term)) : g.items }))
      .filter((g) => g.items.length > 0),
    [term],
  );

  return (
    <div ref={root} className="absolute top-full left-0 z-30 mt-1 w-72 rounded-(--radius-panel) border border-(--color-line) bg-(--color-surface) p-2 shadow-(--shadow-pop)" role="dialog" aria-label="Pick an emoji">
      <input
        ref={search} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search — thanks, tick, cart…" aria-label="Search emoji"
        className="mb-2 w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2 py-1 text-small outline-none focus:border-(--color-brand)"
      />
      <div className="max-h-56 overflow-y-auto">
        {groups.length === 0 && <p className="px-1 py-3 text-center text-small text-(--color-muted)">Nothing matches.</p>}
        {groups.map((g) => (
          <div key={g.label} className="mb-1.5">
            <p className="px-1 text-micro font-medium tracking-wide text-(--color-faint) uppercase">{g.label}</p>
            <div className="grid grid-cols-8">
              {g.items.map(([emoji, name]) => (
                <button key={emoji} type="button" onClick={() => onPick(emoji)} title={name} aria-label={name}
                  className="tap grid h-8 place-items-center rounded text-lg leading-none hover:bg-(--color-sunken)">
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
